import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Check, ArrowRight, Shield, Clock, FileCheck, Star, AlertCircle, ExternalLink, Stethoscope, Home, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Layout } from '../components/layout/Layout';

export default function Pricing() {
  const location = useLocation();

  // Scroll to packages section if hash is present
  useEffect(() => {
    if (location.hash === '#packages') {
      const element = document.getElementById('packages');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  const features = [
    'Full credit report analysis',
    'Dispute letters to all 3 bureaus',
    'Charge-off dispute assistance',
    'Collection removal assistance',
    'Personal information corrections',
    'Public record disputes',
    'Credit inquiry disputes',
    '35-day progress updates',
    'Dedicated support team',
    'Secure document portal',
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-slate-50" data-testid="pricing-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-amber-700 font-medium mb-2">Simple Pricing</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Transparent & Affordable Credit Restoration
            </h1>
            <p className="text-lg text-slate-600">
              No hidden fees. No surprises. Just results-focused credit restoration 
              services at a price that makes sense.
            </p>
          </div>
        </div>
      </section>

      {/* Credit Restoration Service Fees */}
      <section id="packages" className="py-24 bg-white" data-testid="restoration-pricing">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-amber-700 font-medium mb-2">Credit Restoration Services</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Choose Your Restoration Package
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our team is ready to help you dispute inaccurate information and work toward 
              restoring your credit. Choose the package that fits your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            {/* Basic Restoration */}
            <Card className="border-slate-200 shadow-lg card-hover">
              <CardHeader className="text-center pb-4">
                <CardTitle className="font-heading text-xl text-slate-600">
                  Basic Restoration
                </CardTitle>
                <div className="mt-4">
                  <span className="text-5xl font-bold text-slate-900">$297</span>
                  <span className="text-slate-600 ml-2">one-time</span>
                </div>
                <CardDescription className="mt-2">
                  Up to 3 months service period
                </CardDescription>
                <p className="text-xs text-amber-700 mt-2 font-medium">
                  + $29/month credit monitoring required
                </p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Collection Disputes</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Personal Information Corrections</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">35-day progress updates</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Dispute letters to all 3 bureaus</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-amber-600" />
                    <span className="text-slate-700">Timeline: Up to 3 months</span>
                  </li>
                </ul>
                <a 
                  href="https://buy.stripe.com/fZu14m5QG9qa2It0LNco013"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full relative z-10"
                >
                  <Button 
                    className="w-full bg-slate-900 hover:bg-slate-800" 
                    data-testid="basic-restoration-btn"
                  >
                    Choose Basic - $297
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Premium Restoration */}
            <Card className="border-amber-300 shadow-xl pricing-card-glow relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-amber-600 text-white text-xs font-semibold px-4 py-1 rounded-full flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  BEST VALUE
                </span>
              </div>
              <CardHeader className="text-center pb-4">
                <CardTitle className="font-heading text-xl text-slate-600">
                  Premium Restoration
                </CardTitle>
                <div className="mt-4">
                  <span className="text-5xl font-bold text-slate-900">$497</span>
                  <span className="text-slate-600 ml-2">one-time</span>
                </div>
                <CardDescription className="mt-2">
                  6 months comprehensive service
                </CardDescription>
                <p className="text-xs text-amber-700 mt-2 font-medium">
                  + $29/month credit monitoring required
                </p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Charge-Off Removal</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Collection Disputes</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Credit Inquiry Disputes</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Late Payment Disputes</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Public Records</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Personal Information Corrections</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-amber-600" />
                    <span className="text-slate-700">Timeline: 6 months</span>
                  </li>
                </ul>
                <a 
                  href="https://buy.stripe.com/00weVcencbyi6YJ3XZco014"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full relative z-10"
                >
                  <Button 
                    className="w-full bg-amber-600 hover:bg-amber-700" 
                    data-testid="full-restoration-btn"
                  >
                    Choose Premium Restoration - $497
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mb-8">
            <p className="text-slate-600">Or explore our specialized program:</p>
          </div>

          {/* Homeownership Program for Nurses */}
          <div className="max-w-3xl mx-auto">
            <Card className="border-teal-400 shadow-2xl relative overflow-hidden" data-testid="nurses-homeownership-card">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-500 via-teal-400 to-amber-500"></div>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-teal-600 text-white text-xs font-semibold px-4 py-1 rounded-full flex items-center gap-1">
                  <Stethoscope className="h-3 w-3" />
                  FOR NURSES
                </span>
              </div>
              <CardHeader className="text-center pb-4 pt-8">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                    <Stethoscope className="h-8 w-8 text-teal-600" />
                  </div>
                </div>
                <CardTitle className="font-heading text-2xl text-slate-900">
                  9-Month Homeownership Program for Nurses (Healthcare Workers)
                </CardTitle>
                <p className="text-teal-700 font-medium mt-2">
                  Comprehensive Credit Assessment & Personalized Strategy
                </p>
                <div className="mt-4">
                  <span className="text-5xl font-bold text-slate-900">$797</span>
                  <span className="text-slate-600 ml-2">one-time</span>
                </div>
                <CardDescription className="mt-2">
                  9-month comprehensive service tailored for healthcare professionals
                </CardDescription>
                <p className="text-xs text-amber-700 mt-2 font-medium">
                  + $29/month credit monitoring required
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">In-depth credit report analysis from all 3 bureaus</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">Customized dispute strategy targeting negative items</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">Focus on collections, charge-offs, late payments & inquiries</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">Credit utilization guidance & tips to raise score</span>
                    </li>
                  </ul>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">Monthly progress check-ins (via text, email, or call)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">Education on credit mix & age of accounts</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">Ongoing support throughout the 9-month journey</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Home className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700 font-medium">Goal: Mortgage-ready credit for homeownership</span>
                    </li>
                  </ul>
                </div>
                <a 
                  href="https://buy.stripe.com/fZubJ04MC31Mfvf8efco015"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full"
                >
                  <Button 
                    className="w-full bg-teal-600 hover:bg-teal-700 text-lg py-6" 
                    data-testid="nurses-homeownership-btn"
                  >
                    <Stethoscope className="mr-2 h-5 w-5" />
                    Enroll in Nurses Program - $797
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Credit Monitoring Section */}
      <section className="py-24 bg-slate-50" data-testid="monitoring-pricing">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-amber-700 font-medium mb-2">Required For All Packages</p>
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Credit Monitoring
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Credit monitoring is mandatory for all credit restoration services to track your progress.
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            {/* Credit Monitoring */}
            <Card className="border-teal-300 shadow-xl relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-teal-600 text-white text-xs font-semibold px-4 py-1 rounded-full">
                  REQUIRED
                </span>
              </div>
              <CardHeader className="text-center pb-4">
                <CardTitle className="font-heading text-xl text-slate-600">
                  Credit Monitoring
                </CardTitle>
                <div className="mt-4">
                  <span className="text-5xl font-bold text-slate-900">$29</span>
                  <span className="text-slate-600 ml-2">/month</span>
                </div>
                <CardDescription className="mt-4">
                  Required with all restoration packages
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Account setup & portal access</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Credit score tracking</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">35-day progress updates</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Real-time credit alerts</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-teal-600" />
                    <span className="text-slate-700">Dedicated support</span>
                  </li>
                </ul>
                <Link to="/register">
                  <Button 
                    className="w-full bg-teal-600 hover:bg-teal-700" 
                    data-testid="signup-monitoring-btn"
                  >
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-50" data-testid="pricing-features">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Everything Included
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our comprehensive credit restoration service includes everything you need to 
              improve your credit profile.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="flex items-center gap-3 bg-white p-4 rounded-lg border border-slate-200"
              >
                <Check className="h-5 w-5 text-teal-600 flex-shrink-0" />
                <span className="text-slate-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white" data-testid="pricing-process">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Simple 4-Step Process
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <a href="https://buy.stripe.com/aFabJ0enc0TEerb667co016" target="_blank" rel="noopener noreferrer" className="text-center group cursor-pointer" data-testid="step-1-link">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-200 transition-colors">
                <span className="text-2xl font-bold text-amber-700">1</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Select Package</h3>
              <p className="text-sm text-slate-600">Choose $297 or $497 restoration</p>
            </a>
            <a href="https://buy.stripe.com/fZubJ04MC31Mfvf8efco015" target="_blank" rel="noopener noreferrer" className="text-center group cursor-pointer" data-testid="step-2-link">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-200 transition-colors">
                <span className="text-2xl font-bold text-amber-700">2</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Subscribe to Monitoring</h3>
              <p className="text-sm text-slate-600">$29/month credit monitoring</p>
            </a>
            <a href="https://buy.stripe.com/00weVcencbyi6YJ3XZco014" target="_blank" rel="noopener noreferrer" className="text-center group cursor-pointer" data-testid="step-3-link">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-200 transition-colors">
                <span className="text-2xl font-bold text-amber-700">3</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Upload Documents</h3>
              <p className="text-sm text-slate-600">ID, proof of residence, SSN</p>
            </a>
            <a href="https://buy.stripe.com/fZu14m5QG9qa2It0LNco013" target="_blank" rel="noopener noreferrer" className="text-center group cursor-pointer" data-testid="step-4-link">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-200 transition-colors">
                <span className="text-2xl font-bold text-amber-700">4</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Track Progress</h3>
              <p className="text-sm text-slate-600">Receive 35-day updates</p>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 text-white" data-testid="pricing-cta">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Start Your Credit Restoration Journey Today
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Take the first step toward financial freedom. Join hundreds of 
            satisfied clients who have improved their credit with our help.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 btn-active">
                Get Started Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/policy">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                View Our Policies
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

